import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, TextInput } from "@opendate-app/setlist";

import FanFilterGroupForm, { countConditions } from "./FanFilters/FanFilterGroupForm";
import AiSegmentPrompt from "./AiSegmentPrompt";
import AudienceEstimate from "./AudienceEstimate";
import { toastError } from "../../../shared/toastHelper";

const styles = {
  layout: { display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" },
  main: { flex: "1 1 600px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 },
  divider: { display: "flex", alignItems: "center", gap: 12, fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary, #717680)" },
  dividerLine: { flex: 1, height: 1, background: "var(--sl-border-secondary, #E9EAEB)" },
};

const pluralize = (count, noun) => `${count} ${count === 1 ? noun : `${noun}s`}`;

const DataDrivenSegmentForm = ({
  csrfToken,
  team,
  segment,
  setSegment,
  showSegmentNameInput,
  fanFilterGroupId,
  setFanFilterGroupId,
  onValidChangeCallback=() => {},
  aiSegmentBuilder = { enabled: false, examples: [] },
  ensureSegment,
  showAudienceEstimate = false,
}) => {
  const [fanFilterGroup, setFanFilterGroup] = useState({});
  const nameSaveRef = useRef(null);
  const pendingNameRef = useRef(null);
  const nameSaveRequestRef = useRef(Promise.resolve());
  const [justBuilt, setJustBuilt] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildBanner, setBuildBanner] = useState(null);
  const [estimateKey, setEstimateKey] = useState(0);
  const conditionsRef = useRef(null);

  useEffect(() => {
    conditionsRef.current?.toggleAttribute("inert", building);
  }, [building, fanFilterGroup.id]);

  const cancelNameSave = () => {
    clearTimeout(nameSaveRef.current);
    pendingNameRef.current = null;
  };

  const flushNameSave = () => {
    const pending = pendingNameRef.current;
    cancelNameSave();
    if (!pending) return nameSaveRequestRef.current;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = pending.csrfToken;
    nameSaveRequestRef.current = axios.put(pending.url, { segment: { name: pending.name } })
      .catch((error) => toastError((error.response?.data || []).join?.(", ") || "Couldn't save the segment name."));
    return nameSaveRequestRef.current;
  };

  useEffect(() => () => {
    flushNameSave();
  }, []);

  const saveName = (name) => {
    cancelNameSave();
    if (!segment.id || !name.trim()) return;
    pendingNameRef.current = { url: `/teams/${team.id}/segments/${segment.id}`, name, csrfToken };
    nameSaveRef.current = setTimeout(flushNameSave, 500);
  };

  const handleValidChange = () => {
    setEstimateKey((key) => key + 1);
    onValidChangeCallback();
  };

  const applyAiResult = ({ segmentName, fanFilterGroup: builtGroup, prompt, rationale, skippedCount = 0 }) => {
    cancelNameSave();
    setSegment((current) => ({ ...current, name: segmentName }));
    setFanFilterGroup(builtGroup);
    setFanFilterGroupId(builtGroup.id);
    setBuildBanner({ prompt, rationale, skippedCount, conditionCount: countConditions(builtGroup) });
    handleValidChange();
    setJustBuilt(true);
    setTimeout(() => setJustBuilt(false), 1500);
  };

  const prepareBuild = () => (segment.id ? flushNameSave().then(() => segment.id) : ensureSegment());

  const hasConditions = countConditions(fanFilterGroup) > 0 || (fanFilterGroup.children || []).length > 0;

  useEffect(() => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    if (fanFilterGroupId) {
      axios.get(`/teams/${team.id}/fan_filter_groups/${fanFilterGroupId}`)
        .then((data) => {
          setFanFilterGroup(data["data"]);
          setFanFilterGroupId(data["data"]["id"]);
        })
    } else {
      axios.post(`/teams/${team.id}/fan_filter_groups`, { fan_filter_group: { match_condition: "All" } })
        .then((data) => {
          setFanFilterGroup(data["data"]);
          setFanFilterGroupId(data["data"]["id"]);
        })
        .catch((error) => {
          const errors = error.response.data;
          toastError(errors.join(", "));
        });
    }
  }, []);

  const showPrompt = aiSegmentBuilder.enabled && (segment.id || ensureSegment) && fanFilterGroup.id;

  const main = (
    <div style={styles.main}>
      {showSegmentNameInput ? (
        <TextInput
          label="Name your segment"
          value={segment.name || ""}
          disabled={building}
          onChange={(e) => {
            setSegment({
              ...segment,
              name: e.target.value
            });
            saveName(e.target.value);
          }}
        />
      ) : null}
      {showPrompt ? (
        <>
          <AiSegmentPrompt
            csrfToken={csrfToken}
            team={team}
            segmentId={segment.id}
            examples={aiSegmentBuilder.examples || []}
            placeholder={aiSegmentBuilder.placeholder ? `e.g. ${aiSegmentBuilder.placeholder}` : undefined}
            hasConditions={hasConditions}
            onBuilt={applyAiResult}
            beforeBuild={prepareBuild}
            onBuildingChange={setBuilding}
          />
          <div style={styles.divider}>
            <span style={styles.dividerLine} />
            or build it yourself
            <span style={styles.dividerLine} />
          </div>
        </>
      ) : null}
      {buildBanner ? (
        <Alert
          color="brand"
          title={`Conductor built ${pluralize(buildBanner.conditionCount, "condition")} from “${buildBanner.prompt}”. Review below.`}
          description={[
            buildBanner.rationale,
            buildBanner.skippedCount ? `Skipped ${pluralize(buildBanner.skippedCount, "condition")} it couldn't match.` : null,
          ].filter(Boolean).join(" ") || undefined}
          onDismiss={() => setBuildBanner(null)}
        />
      ) : null}
      {!!fanFilterGroup.id ? (
        <div
          ref={conditionsRef}
          aria-busy={building ? "true" : undefined}
          style={{
            borderRadius: 12,
            transition: "box-shadow 600ms ease",
            boxShadow: justBuilt ? "0 0 0 2px var(--sl-border-brand, #2A9CE4)" : "none",
            pointerEvents: building ? "none" : undefined,
            opacity: building ? 0.6 : undefined,
          }}
        >
          <FanFilterGroupForm
            csrfToken={csrfToken}
            team={team}
            fanFilterGroup={fanFilterGroup}
            onValidChangeCallback={handleValidChange}
            onGroupChange={setFanFilterGroup}
          />
        </div>
      ) : null}
    </div>
  );

  if (!showAudienceEstimate) return main;

  return (
    <div style={styles.layout}>
      {main}
      <AudienceEstimate team={team} fanFilterGroupId={fanFilterGroup.id} refreshKey={estimateKey} building={building} contactLabel={aiSegmentBuilder.contactLabel} />
    </div>
  );
};

export default DataDrivenSegmentForm;
